import { listCredits } from '@/modules/credits/services/creditsServices'
import { listPaymentReconciliations } from '@/modules/paymentReconciliations/services/paymentReconciliationServices'
import { listRedirections } from '@/modules/redirections/services/redirectionsServices'
import { listRepayments } from '@/modules/repayments/services/repaymentsServices'
import { listUsers } from '@/modules/users/services/userServices'
import { Guarantor } from '@/shared/models/companyModel'
import { Credit } from '@/shared/models/creditModels'
import { Debtor, Identifier } from '@/shared/models/debtorModels'
import { InvoiceGateway } from '@/shared/models/invoiceModels'
import { LoanGateway } from '@/shared/models/loanModels'
import { RedirectionGateway } from '@/shared/models/redirectionModels'
import { Repayment } from '@/shared/models/repaymentModel'
import { listDebtors } from '@/shared/services/debtorServices'
import { listInvoices } from '@/shared/services/invoicesServices'
import { listLoans } from '@/shared/services/loansServices'
import { listReceivables } from '@/shared/services/receivableServices'
import { RequestResponse } from '@/shared/services/requestServices'
import { trackEvent } from '@/shared/utils/analyticsUtils'
import { batchFetch } from '@/shared/utils/requestsUtils'
import { In, QueryStringInput } from '@hello-aria/query-string'
import { isEmpty, isNil, uniq } from 'lodash'
import { FormatResult, formatRows } from '../utils/exportUtils'

export type ExportOptions = { query?: QueryStringInput; signal?: AbortSignal } | undefined

// A loan export can name thousands of debtors, more than one request URL holds.
const listDebtorsInBatches = batchFetch(listDebtors)

// makalu-api answers 20 items unless asked for more, and accepts 100 at most.
const MAKALU_API_MAX_PAGE_SIZE = 100

// A lookup asks for a full page, so every id finds its item. batchFetch splits a longer list into pages of 100.
const toIdLookup = (ids: string[]): QueryStringInput => ({
    filters: [In('id', ids)],
    pagination: { first: MAKALU_API_MAX_PAGE_SIZE }
})

export async function fetchAllPages<TItem>(
    fetchPage: (q: QueryStringInput, signal?: AbortSignal) => Promise<RequestResponse<TItem[]> | undefined>,
    baseQuery: QueryStringInput = {},
    signal?: AbortSignal,
    pageSize = MAKALU_API_MAX_PAGE_SIZE
): Promise<TItem[]> {
    let after: string | undefined = undefined
    let hasNextPage = true
    const all: TItem[] = []

    while (hasNextPage) {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError')
        }

        const q: QueryStringInput = { ...baseQuery, pagination: { first: pageSize, after } }

        const res = await fetchPage(q, signal)
        const items = res?.body ?? []
        if (items.length) all.push(...items)

        hasNextPage = !!res?.pagination?.hasNextPage
        after = res?.pagination?.endCursor
    }

    return all
}

export type ExportRedirection = Pick<
    RedirectionGateway,
    'id' | 'createdAt' | 'currency' | 'amount' | 'status' | 'label'
> & {
    repaymentId: Repayment['id'][]
}

export const exportRedirections = async (
    companyId: string,
    options?: ExportOptions
): Promise<FormatResult<ExportRedirection, 'amount', 'createdAt'>[]> => {
    const { query, signal } = options || {}
    const redirections = await fetchAllPages(
        (q, s) => listRedirections({ companyId, query: q, signal: s }),
        query,
        signal
    )

    if (isEmpty(redirections)) {
        return []
    }

    const paymentReconciliationIds = redirections.map((redirection) => redirection.paymentReconciliationId)
    const sanitizedPaymentReconciliationIds = uniq(paymentReconciliationIds)

    const paymentReconciliations = await listPaymentReconciliations({
        companyId,
        query: toIdLookup(sanitizedPaymentReconciliationIds),
        signal
    })

    const mappedResponse: ExportRedirection[] =
        redirections.map((redirection) => ({
            id: redirection.id,
            createdAt: redirection.createdAt,
            currency: redirection.currency,
            amount: redirection.amount,
            status: redirection.status,
            label: redirection.label,
            repaymentId:
                paymentReconciliations?.body
                    ?.find((pr) => pr.id === redirection.paymentReconciliationId)
                    ?.repayments.map((repayment) => repayment.id) ?? []
        })) ?? []

    const formattedRows = formatRows(mappedResponse, {
        locale: 'en',
        amountFields: ['amount'],
        dateFields: ['createdAt']
    })

    trackEvent('Export', { resource: 'redirections', query })

    return formattedRows
}

export type ExportCreditLimit = Pick<Credit, 'id' | 'updatedAt' | 'status' | 'claimedCurrency'> & {
    allocationGuarantor: Guarantor
    allocationAmount: number
    allocationOutstandingAmount: number
    debtorName: Debtor['name'] | null
    debtorIdentifierValue: Identifier['value'] | null
    debtorIdentifierType: Identifier['type'] | null
}

export const exportCreditLimits = async (
    companyId: string,
    options?: ExportOptions
): Promise<FormatResult<ExportCreditLimit, 'allocationAmount' | 'allocationOutstandingAmount', 'updatedAt'>[]> => {
    const { query, signal } = options || {}
    const credits = await fetchAllPages((q, s) => listCredits({ companyId, query: q, signal: s }), query, signal)

    if (isEmpty(credits)) {
        return []
    }

    const mappedResponse: ExportCreditLimit[] = credits.flatMap((credit) => {
        if (isNil(credit.allocations) || isEmpty(credit.allocations)) {
            return []
        }

        return credit.allocations.map((alloc) => {
            const row: ExportCreditLimit = {
                id: credit.id,
                updatedAt: credit.updatedAt,
                status: credit.status,
                claimedCurrency: credit.claimedCurrency,

                allocationGuarantor: alloc.guarantor,
                allocationOutstandingAmount: alloc.outstandingAmount ?? 0,
                allocationAmount: alloc.amount ?? 0,

                debtorName: credit.debtor?.name ?? null,
                debtorIdentifierValue: credit.debtorIdentifier.value,
                debtorIdentifierType: credit.debtorIdentifier.type
            }

            return row
        })
    })

    const formattedRows = formatRows(mappedResponse, {
        locale: 'en',
        amountFields: ['allocationAmount', 'allocationOutstandingAmount'],
        dateFields: ['updatedAt']
    })

    trackEvent('Export', { resource: 'creditLimits', query })

    return formattedRows
}

export type ExportRepayment = Pick<
    Repayment,
    'id' | 'createdAt' | 'sender' | 'label' | 'amount' | 'currency' | 'allocationsStatus'
> & {
    transactionStatus: Repayment['status']
    invoiceNumbers: string[]
    loanIds: string[]
}

export const exportRepayments = async (
    companyId: string,
    options?: ExportOptions
): Promise<FormatResult<ExportRepayment, 'amount', 'createdAt'>[]> => {
    const { query, signal } = options || {}
    const repayments = await fetchAllPages((q, s) => listRepayments(companyId, q, s), query, signal)

    if (isEmpty(repayments)) {
        return []
    }

    const paymentReconciliationIds = repayments.flatMap(
        (repayment) => repayment.paymentReconciliations?.map((pr) => pr.id) ?? []
    )
    const sanitizedPaymentReconciliationIds = uniq(paymentReconciliationIds).filter((id) => !isNil(id))

    const paymentReconciliations = !isEmpty(sanitizedPaymentReconciliationIds)
        ? await listPaymentReconciliations({
              companyId,
              query: toIdLookup(sanitizedPaymentReconciliationIds),
              signal
          })
        : undefined

    const loanIds =
        paymentReconciliations?.body?.flatMap((pr) => pr.loans.map((loan) => loan.id))?.filter((id) => !isNil(id)) ?? []
    const sanitizedLoanIds = uniq(loanIds)

    const loansResponse = !isEmpty(sanitizedLoanIds)
        ? await listLoans({
              companyId,
              hasAttachments: false,
              query: toIdLookup(sanitizedLoanIds),
              signal
          })
        : undefined

    const invoiceIds =
        loansResponse?.body?.flatMap((loan) => loan.invoiceId).filter((invoiceId) => !isNil(invoiceId)) ?? []
    const sanitizedInvoiceIds = uniq(invoiceIds)

    const invoicesResponse = !isEmpty(sanitizedInvoiceIds)
        ? await listInvoices({
              companyId,
              query: toIdLookup(sanitizedInvoiceIds),
              hasAttachments: false,
              signal
          })
        : undefined

    const mappedResponse: ExportRepayment[] = repayments.map((repayment) => {
        const relatedPaymentReconciliationIds = repayment.paymentReconciliations?.map((pr) => pr.id) ?? []
        const relatedPaymentReconciliations =
            paymentReconciliations?.body?.filter((pr) => relatedPaymentReconciliationIds.includes(pr.id)) ?? []

        const relatedLoanIds = relatedPaymentReconciliations.flatMap((pr) => pr.loans.map((loan) => loan.id))
        const relatedLoans = loansResponse?.body?.filter((loan) => relatedLoanIds.includes(loan.id)) ?? []

        const relatedInvoiceIds = relatedLoans.flatMap((loan) => loan.invoiceId)
        const relatedInvoices =
            invoicesResponse?.body?.filter((invoice) => relatedInvoiceIds.includes(invoice.id)) ?? []

        return {
            id: repayment.id,
            createdAt: repayment.createdAt,
            sender: repayment.sender,
            label: repayment.label,
            amount: repayment.amount,
            currency: repayment.currency,
            transactionStatus: repayment.status,
            allocationsStatus: repayment.allocationsStatus,
            invoiceNumbers: relatedInvoices.map((invoice) => invoice.invoiceNumber),
            loanIds: relatedLoans.map((loan) => loan.id)
        }
    })

    const formattedRows = formatRows(mappedResponse, {
        locale: 'en',
        amountFields: ['amount'],
        dateFields: ['createdAt']
    })

    trackEvent('Export', { resource: 'repayments', query })

    return formattedRows
}

export type ExportLoan = Pick<
    LoanGateway,
    'id' | 'status' | 'amount' | 'currency' | 'createdAt' | 'paidAt' | 'repaidAt'
> & {
    billingType: 'invoice' | 'receivable' | undefined
    billingNumber: string | undefined
    billingDate: string | undefined

    invoiceDueDate: string | undefined
    invoiceAmount: number | undefined
    invoicePaymentTermsType: InvoiceGateway['paymentTermType'] | undefined
    invoiceRepaymentPeriod: number | undefined

    debtorName: string | undefined
    debtorIdentifierValue: string | undefined
    debtorIdentifierType: string | undefined

    beneficiaryCompanyName: string | undefined
    beneficiaryContactName: string | undefined
    beneficiaryContactEmail: string | undefined
    beneficiaryBusinessIdentifierValue: string | undefined
    beneficiaryBusinessIdentifierType: string | undefined

    blockReasons: string | undefined
    refusalReasons: string | undefined
}

export const exportLoans = async (
    companyId: string,
    options?: ExportOptions
): Promise<
    FormatResult<
        ExportLoan,
        'amount' | 'invoiceAmount',
        'createdAt' | 'paidAt' | 'repaidAt' | 'billingDate' | 'invoiceDueDate'
    >[]
> => {
    const { query, signal } = options || {}

    const loans = await fetchAllPages(
        (q, s) => listLoans({ companyId, query: q, hasAttachments: false, signal: s }),
        query,
        signal
    )

    const sanitizedReceivablesIds = uniq(loans.map((loan) => loan.receivableId).filter((v): v is string => !isNil(v)))
    const sanitizedInvoiceIds = uniq(loans.map((loan) => loan.invoiceId).filter((v): v is string => !isNil(v)))
    const sanitizedUserIds = uniq(loans.map((loan) => loan.userId).filter((v): v is string => !isNil(v)))

    const receivablesPromise = !isEmpty(sanitizedReceivablesIds)
        ? listReceivables({
              companyId,
              query: toIdLookup(sanitizedReceivablesIds),
              signal
          })
        : Promise.resolve(undefined)

    const invoicesPromise = !isEmpty(sanitizedInvoiceIds)
        ? listInvoices({
              companyId,
              query: toIdLookup(sanitizedInvoiceIds),
              hasAttachments: false,
              signal
          })
        : Promise.resolve(undefined)

    const usersPromise = !isEmpty(sanitizedUserIds)
        ? listUsers({
              companyId,
              query: toIdLookup(sanitizedUserIds),
              signal
          })
        : Promise.resolve(undefined)

    const [receivablePromise, invoicesResponse, usersResponse] = await Promise.all([
        receivablesPromise,
        invoicesPromise,
        usersPromise
    ])

    // makalu-api sends the id of the debtor of a receivable, not its name.
    const receivableDebtorIds = uniq((receivablePromise?.body ?? []).map((receivable) => receivable.debtorId))
    const receivableDebtorsResponse = !isEmpty(receivableDebtorIds)
        ? await listDebtorsInBatches({ companyId, query: toIdLookup(receivableDebtorIds) })
        : undefined

    const invoiceById = new Map((invoicesResponse?.body ?? []).map((inv) => [inv.id, inv]))
    const userById = new Map((usersResponse?.body ?? []).map((u) => [u.id, u]))
    const debtorById = new Map((receivableDebtorsResponse?.body ?? []).map((debtor) => [debtor.id, debtor]))

    const mappedResponse: ExportLoan[] = loans.map((loan) => {
        const relatedReceivable = loan.receivableId
            ? receivablePromise?.body?.find((rec) => rec.id === loan.receivableId)
            : null
        const relatedInvoice = loan.invoiceId ? invoiceById.get(loan.invoiceId) : null
        const relatedUser = loan.userId ? (userById.get(loan.userId) ?? null) : null
        const invoiceBillingType = relatedInvoice ? 'invoice' : undefined

        const blockReasons = loan.activeBlockReasons?.map((r) => r.code).join(', ') || undefined
        const refusalReasons = loan.refusalReasons?.map((r) => r.code).join(', ') || undefined

        return {
            id: loan.id,
            status: loan.status,
            amount: loan.amount,
            currency: loan.currency,
            createdAt: loan.createdAt,
            paidAt: loan.paidAt,
            repaidAt: loan.repaidAt,

            billingType: relatedReceivable ? 'receivable' : invoiceBillingType,
            billingNumber: relatedReceivable?.reference ?? relatedInvoice?.invoiceNumber,
            billingDate: relatedReceivable?.deliveredAt ?? relatedInvoice?.invoiceDate,

            invoiceDueDate: relatedReceivable?.expectedInvoiceDueDate ?? relatedInvoice?.dueDate,
            invoiceAmount: relatedReceivable?.totalAmountIncludingTaxes ?? relatedInvoice?.totalAmountIncludingTaxes,
            invoicePaymentTermsType:
                relatedReceivable?.expectedInvoicePaymentTermType ?? relatedInvoice?.paymentTermType,
            invoiceRepaymentPeriod: relatedReceivable?.expectedInvoicePaymentTerm ?? relatedInvoice?.repaymentPeriod,

            debtorName: relatedReceivable
                ? (debtorById.get(relatedReceivable.debtorId)?.name ?? undefined)
                : (relatedInvoice?.debtorName ?? undefined),
            debtorIdentifierValue: relatedReceivable?.debtorIdentifier?.value ?? relatedInvoice?.debtorIdentifier.value,
            debtorIdentifierType: relatedReceivable?.debtorIdentifier?.type ?? relatedInvoice?.debtorIdentifier.type,

            beneficiaryCompanyName: relatedUser?.businessName,
            beneficiaryContactName: relatedUser?.fullName,
            beneficiaryContactEmail: relatedUser?.email,
            beneficiaryBusinessIdentifierValue: relatedUser?.businessIdentifier?.value,
            beneficiaryBusinessIdentifierType: relatedUser?.businessIdentifier?.type,

            blockReasons,
            refusalReasons
        }
    })

    const formattedRows = formatRows(mappedResponse, {
        locale: 'en',
        amountFields: ['amount', 'invoiceAmount'],
        dateFields: ['createdAt', 'paidAt', 'repaidAt', 'invoiceDueDate', 'billingDate']
    })

    trackEvent('Export', { resource: 'loans', query })

    return formattedRows
}
